import {Box, Grid, Theme, Typography} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {numericQuantity} from 'numeric-quantity';
import React, {FC, memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {useSelector} from 'react-redux';
import maskGroupImg from '../../../../assets/products/product-5/MaskGroup5.png';
import {localized, localizedInterpolation} from '../../../../i18n/i18n';
import {AppState} from '../../../../state/store';
import {
  getUnitLenghtByCulture,
  getValueLengthWithUnitByCulture,
  getValueRangeWithUnitByCulture,
  validUnitsIds,
} from '../../../../utilities/helpers/culture-settings-helper';
import {manuelNumberCheck} from '../../../../utilities/helpers/form-helper';
import {ImageRow} from '../../../components/custom-components/configuration/dynamic-components/image-row';
import {ValueRangeInput} from '../../../components/custom-components/configuration/dynamic-components/value-range-input';
import {liftingUnitType, wallCupboardDepth, wallCupboardHeight, wallCupboardLength} from '../configuration-constants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    grid: {
      width: '686px',
      [theme.breakpoints.down('lg')]: {
        width: '500px',
      },
    },
    gridItem: {
      height: '120px',
    },
  }),
);

export const Product5Step2: FC = memo(() => {
  const classes = useStyles();
  const [currentHeight, setCurrentHeight] = useState<number>(0);
  const {unitsId} = useSelector((store: AppState) => store.urlSettingsReducer.cultureUrlParameters);
  const formContext = useFormContext();
  const myRef = useRef<null | HTMLDivElement>(null);

  const getMinValue = useMemo(() => {
    const type = formContext.getValues(liftingUnitType);
    if (type === getValueRangeWithUnitByCulture(validUnitsIds.si, '400', '1200')) {
      return '400';
    } else if (type === getValueRangeWithUnitByCulture(validUnitsIds.si, '1201', '2400')) {
      return '1202';
    } else if (type === getValueRangeWithUnitByCulture(validUnitsIds.iu, '400', '1200')) {
      return '400';
    } else if (type === getValueRangeWithUnitByCulture(validUnitsIds.iu, '1201', '2400')) {
      return '1202';
    } else {
      return '0';
    }
  }, [formContext]);

  const getMaxValue = useMemo(() => {
    const type = formContext.getValues(liftingUnitType);
    if (type === getValueRangeWithUnitByCulture(validUnitsIds.si, '400', '1200')) {
      return '1200';
    } else if (type === getValueRangeWithUnitByCulture(validUnitsIds.si, '1201', '2400')) {
      return '2400';
    } else if (type === getValueRangeWithUnitByCulture(validUnitsIds.iu, '400', '1200')) {
      return '1200';
    } else if (type === getValueRangeWithUnitByCulture(validUnitsIds.iu, '1201', '2400')) {
      return '2400';
    } else {
      return '0';
    }
  }, [formContext]);

  const betweenMinAndMaxLength = useCallback(
    (event: any) => {
      manuelNumberCheck(
        event.target.name,
        event.target.value,
        getUnitLenghtByCulture(unitsId, getMinValue),
        getUnitLenghtByCulture(unitsId, getMaxValue),
        unitsId,
        formContext,
      );
    },

    [formContext, getMaxValue, getMinValue, unitsId],
  );

  const between576And924 = useCallback(
    (event: any) => {
      let uniformStringValue = event.target.value.replace(',', '.');
      setCurrentHeight(numericQuantity(uniformStringValue));

      manuelNumberCheck(
        event.target.name,
        event.target.value,
        getUnitLenghtByCulture(unitsId, '576'),
        getUnitLenghtByCulture(unitsId, '924'),
        unitsId,
        formContext,
      );
    },
    [formContext, unitsId],
  );

  const between0And350 = useCallback(
    (event: any) => {
      manuelNumberCheck(
        event.target.name,
        event.target.value,
        getUnitLenghtByCulture(unitsId, '0'),
        getUnitLenghtByCulture(unitsId, '350'),
        unitsId,
        formContext,
      );
    },
    [formContext, unitsId],
  );

  useEffect(() => {
    if (myRef && myRef.current) {
      myRef.current.scrollIntoView({block: 'end'});
    }
  }, []);

  const ExtenderNotificationRow = useCallback(() => {
    return (
      <React.Fragment>
        <Grid item xs={12}>
          <Typography variant="body3" color="error">
            {localized('RememberExtender')}
          </Typography>
        </Grid>
      </React.Fragment>
    );
  }, []);

  useEffect(() => {
    let length = formContext.getValues(wallCupboardLength);
    length && formContext.trigger(wallCupboardLength);
    let height = formContext.getValues(wallCupboardHeight);
    height && formContext.trigger(wallCupboardHeight);
    let depth = formContext.getValues(wallCupboardDepth);
    depth && formContext.trigger(wallCupboardDepth);
  }, []);

  return (
    <>
      <Box display="flex" justifyContent="center" alignItems="center" sx={{flexGrow: 1}}>
        <Grid container spacing={2} className={classes.grid}>
          <Grid ref={myRef} container item spacing={6}>
            <ImageRow imgString={maskGroupImg} />
          </Grid>

          <Grid container item spacing={6}>
            <ValueRangeInput
              formName={wallCupboardLength}
              minValue={getMinValue}
              maxValue={getMaxValue}
              textInputLabel={localized('LengthOfWallCabinet') + ' (E)'}
              tooltipLabel={localizedInterpolation('ToolTipProduct5Step2_1', {
                value1: getValueLengthWithUnitByCulture(unitsId, getMinValue),
                value2: getValueLengthWithUnitByCulture(unitsId, getMaxValue),
              })}
              formOnChange={betweenMinAndMaxLength}
            />
          </Grid>

          <Grid container item spacing={6}>
            <ValueRangeInput
              formName={wallCupboardHeight}
              minValue={'576'}
              maxValue={'924'}
              textInputLabel={localized('HeigthOfWallCabinet') + ' (F)'}
              tooltipLabel={localizedInterpolation('ToolTipProduct5Step2_2', {
                value1: getValueLengthWithUnitByCulture(unitsId, '576'),
                value2: getValueLengthWithUnitByCulture(unitsId, '730'),
                value3: getValueLengthWithUnitByCulture(unitsId, '731'),
                value4: getValueLengthWithUnitByCulture(unitsId, '920'),
              })}
              formOnChange={between576And924}
            />
          </Grid>

          {currentHeight > 729 && currentHeight < 925 && (
            <Grid container item spacing={6}>
              <ExtenderNotificationRow></ExtenderNotificationRow>
            </Grid>
          )}

          <Grid container item spacing={6}>
            <ValueRangeInput
              formName={wallCupboardDepth}
              minValue={'2'}
              maxValue={'350'}
              textInputLabel={localized('DepthOfWallCabinet') + ' (G)'}
              tooltipLabel={localizedInterpolation('ToolTipProduct5Step2_3', {
                value1: getValueLengthWithUnitByCulture(unitsId, '350'),
              })}
              hintLabel={
                '(' +
                localizedInterpolation('MaxX', {
                  value1: getValueLengthWithUnitByCulture(unitsId, '350'),
                }) +
                ')'
              }
              formOnChange={between0And350}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
});
